/**
 * Saf donusum: WelcomeContent (i18n anahtarlari) + bir t() fonksiyonu ->
 * sablonun dogrudan render edebilecegi cozulmus deger nesneleri. Bilesenden
 * (AuthWelcomePanel.vue) ayri tutulmasinin nedeni: bu mantik DOM'a hic
 * dokunmadan test edilebilir olsun (bkz. resolveWelcomeBlocks.spec.ts).
 *
 * FW-131 KAPSAM B ("gorselsiz varyant") — design/current/Auth v2.dc.html
 * satir 403/414: `wantImage = props.gorsel ?? true` sonra
 * `blocks.filter(b => b.type !== 'image' || wantImage)`. Ayni filtre
 * burada `includeImage` secenegiyle uygulanir; yeni bir icerik sistemi
 * DEGIL, bu fonksiyonun mevcut donusum hattinin bir uzantisidir.
 */
import type { WelcomeBlock, WelcomeContent } from './welcomeContent'

export interface WelcomeImageBlockView {
  readonly kind: 'image'
  readonly alt: string
  readonly spec: string | null
}

export interface WelcomeHeadlineBlockView {
  readonly kind: 'headline'
  readonly kicker: string | null
  readonly title: string
  readonly sub: string | null
}

export interface WelcomeModulesBlockView {
  readonly kind: 'modules'
  readonly items: readonly { readonly name: string; readonly line: string }[]
}

export interface WelcomeChecklistBlockView {
  readonly kind: 'checklist'
  readonly items: readonly string[]
}

export interface WelcomeQuoteBlockView {
  readonly kind: 'quote'
  readonly text: string
  readonly name: string
  readonly role: string
  readonly initials: string
}

export type WelcomeBlockView =
  | WelcomeImageBlockView
  | WelcomeHeadlineBlockView
  | WelcomeModulesBlockView
  | WelcomeChecklistBlockView
  | WelcomeQuoteBlockView

export interface ResolvedWelcomeContent {
  readonly blocks: readonly WelcomeBlockView[]
  readonly proof: readonly string[]
}

export type Translate<Key extends string = string> = (key: Key, params?: Record<string, string>) => string

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function resolveBlock<Key extends string>(block: WelcomeBlock<Key>, t: Translate<Key>, headlineTitleParams?: Record<string, string>): WelcomeBlockView {
  switch (block.type) {
    case 'image':
      return { kind: 'image', alt: t(block.altKey), spec: block.specKey ? t(block.specKey) : null }
    case 'headline':
      return {
        kind: 'headline',
        kicker: block.kickerKey ? t(block.kickerKey) : null,
        title: t(block.titleKey, headlineTitleParams),
        sub: block.subKey ? t(block.subKey) : null,
      }
    case 'modules':
      return {
        kind: 'modules',
        items: block.items.map((item) => ({ name: t(item.nameKey), line: t(item.lineKey) })),
      }
    case 'checklist':
      return { kind: 'checklist', items: block.itemKeys.map((key) => t(key)) }
    case 'quote':
      return {
        kind: 'quote',
        text: t(block.textKey),
        name: block.name,
        role: t(block.roleKey),
        initials: initialsOf(block.name),
      }
  }
}

export interface ResolveWelcomeBlocksOptions {
  /** design/current/Auth v2.dc.html `props.gorsel` karsiligi. Verilmezse true (image blogu kalir). */
  readonly includeImage?: boolean
  /**
   * FW-175 — davet akisinin basligi kiraci adini TASIR ({tenant}, bkz.
   * welcomeContent.ts'in authInviteWelcomeContent basligi). Giris akisinin
   * sabit basligi bu parametreyi YOKSAYAR (i18n dizesinde yer tutucu yok);
   * zararsizdir.
   */
  readonly headlineTitleParams?: Record<string, string>
}

export function resolveWelcomeBlocks<Key extends string>(
  content: WelcomeContent<Key>,
  t: Translate<NoInfer<Key>>,
  options: ResolveWelcomeBlocksOptions = {},
): ResolvedWelcomeContent {
  const includeImage = options.includeImage ?? true
  const blocks = content.blocks.filter((block) => block.type !== 'image' || includeImage)
  return {
    blocks: blocks.map((block) => resolveBlock(block, t, options.headlineTitleParams)),
    proof: content.proofKeys.map((key) => t(key)),
  }
}
